import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client/react'
// Centralized lodash imports - much cleaner!
import {
  isEmpty,
  map,
  isUndefined,
  includes,
  slice,
  cloneDeep,
  assign,
  get,
  set,
  sum,
  find,
  concat,
  filter,
  isNil,
  isArray,
  isObject,
  replace,
} from '@/utils/lodash'

import { UPDATE_PROPERTY } from '@/housefolios-components/PropertyContainer/mutations'
import { daysBetween, formatDateNumeric } from '@/utils/date'
import { GET_PROPERTIES } from '@/housefolios-components/Properties/queries'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'
import { getProformaLink } from '@/utils/proforma'

export const usePropertyCard = (property, props) => {
  const [heart, setHeart] = useState(property.favorite)
  const [openCriticalFields, setOpenCriticalFields] = useState(false)
  const [redirectToEdit, setRedirectToEdit] = useState(false)
  const [openAccept, setOpenAccept] = useState(false)
  const [openReject, setOpenReject] = useState(false)
  const [openPITICashData, setOpenPITICashData] = useState(false)
  const [openPITICreativeData, setOpenPITICreativeData] =
    useState(false)

  const [updateProperty] = useMutation(UPDATE_PROPERTY)

  useEffect(() => {
    setHeart(property.favorite)
  }, [property?.favorite, property?._id])

  const handleOpenCriticalFields = (directToEdit = false) => {
    setOpenCriticalFields(true)
    setRedirectToEdit(directToEdit)
  }

  const handleCloseCriticalFields = () => {
    setOpenCriticalFields(false)
  }

  const toggleHeart = () => {
    setHeart(!heart)
    updateProperty({
      variables: {
        id: property._id,
        propertyInput: {
          favorite: !heart,
        },
      },
      refetchQueries: [
        {
          query: GET_PROPERTY,
          variables: { id: property._id },
        },
      ],
    })
  }

  // Calculate marketplace ownership
  const marketplaceOwned =
    (isNil(get(property, 'portfolio.organization._id')) &&
      !props.propertySearch) ||
    get(property, 'portfolio.organization._id') ===
      props?.session?.me?.activeOrg?._id ||
    find(
      concat(
        props.portfolios || [],
        filter(
          props?.session?.me?.activeOrg?.sharedPortfolios,
          (portfolio) => portfolio.sharePermission === 'canEdit',
        ),
      ),
      (portfolio) => portfolio._id === get(property, 'portfolio._id'),
    ) ||
    find(
      filter(
        props?.session?.me?.activeOrg?.sharedProperties,
        (property) => property.sharePermission === 'canEdit',
      ),
      (sharedProperty) => sharedProperty.id === property._id,
    )

  const owned = property.owned && marketplaceOwned

  // Calculate price per square foot
  const pricePerSqft = property?.sqft
    ? Number(
        property.price ||
          get(property, 'acquisition.analysis.resellPrice'),
      ) / property.sqft || 0
    : null

  const createdAt = formatDateNumeric(property.createdAt)

  const today = new Date()
  let endDate = new Date(property.deletedAt)
  endDate.setDate(endDate.getDate() + 7)
  const daysUntilDeleted = daysBetween(today, endDate)
    .toFixed(0)

  let listDate =
    !props.trash && !props.propertySearch && props.marketplace
      ? new Date(property.marketplaces.expirationDate)
      : ''

  if (listDate && listDate instanceof Date) {
    listDate.setDate(listDate.getDate() - 7)
    listDate = formatDateNumeric(listDate)
  }

  const realeflowSoldDate =
    get(property, 'realeflowData.SoldDateText') ||
    get(property, 'realeflowData.SubjectProperty.SoldDateText') ||
    get(property, 'realeflowData.LastSalesDate')

  // PHP send functionality
  const phpSend = (remove = false) => {
    const hostname = import.meta.env.VITE_HOSTNAME

    if (remove) {
      const data = {
        _id: property._id,
        remove_property: remove,
      }
      // ... rest of remove logic
    } else {
      let data = cloneDeep(property)
      // ... rest of data processing logic
    }
  }

  // Analytics calculation
  const getAnalytics = () => {
    if (!property) return []

    if (
      props.propertySearch &&
      !(
        property.marketplaces &&
        !isEmpty(property.marketplaces.approvedMarketplace)
      )
    ) {
      return []
    }

    if (props.public) {
      return map(property?.marketplaceAnalytics, (analytics) => {
        return {
          ...analytics,
          value: analytics.sum
            ? sum(get(property, analytics.target)) /
              (analytics.divisor || 1)
            : get(property, analytics.target) /
              (analytics.divisor || 1),
          arrow: get(property, analytics.target + 'Arrow'),
        }
      })
    }

    // Strategy-based analytics logic
    const strategy = props.marketplace
      ? property?.marketplaceStrategy
      : property?.strategy

    switch (strategy) {
      case 'Build to Rent':
        return getBuildToRentAnalytics()
      case 'Wholesale':
        return getWholesaleAnalytics()
      case 'Astroflip':
        return getAstroflipAnalytics()
      case 'BOR':
        return getBORAnalytics()
      case 'Short Term Rental':
        return getShortTermRentalAnalytics()
      case 'BRRRR':
        return getBRRRRAnalytics()
      case 'Turnkey End Buyer':
        return getTurnkeyAnalytics()
      case 'Fix & Flip':
        return getFixFlipAnalytics()
      default:
        return getBuyHoldAnalytics()
    }
  }

  // Individual analytics functions
  const getBuildToRentAnalytics = () => [
    {
      value:
        get(property, 'acquisition.analytics.B2R.cashFlowB2R', 0) /
        12,
      arrow: get(
        property,
        'acquisition.analytics.B2R.cashFlowB2RArrow',
        0,
      ),
      label: 'Net Cashflow',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.B2R.cashOnCashReturnB2R',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.B2R.cashOnCashReturnB2RArrow',
        0,
      ),
      label: 'CoC ROI',
      type: 'percent',
    },
    {
      value: get(property, 'acquisition.analytics.B2R.IRRB2R', '-'),
      arrow: get(
        property,
        'acquisition.analytics.B2R.IRRB2RArrow',
        0,
      ),
      label: 'IRR',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.B2R.capRateB2R',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.B2R.capRateB2RArrow',
        0,
      ),
      label: 'Yield-on-Cost',
      type: 'percent',
    },
  ]

  const getWholesaleAnalytics = () => [
    {
      value: get(
        property,
        'acquisition.analytics.wholesale.saleProceedsWholesale',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.wholesale.saleProceedsWholesaleArrow',
        0,
      ),
      label: 'Net Profit',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.wholesale.buyerProfitTotal',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.wholesale.buyerProfitArrow',
        0,
      ),
      label: 'Buyer Profit',
      type: 'currency',
    },
  ]

  const getAstroflipAnalytics = () => [
    {
      value: get(
        property,
        'acquisition.analytics.astroFlip.saleProceedsAstroFlip',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.astroFlip.saleProceedsAstroFlipArrow',
        0,
      ),
      label: 'Net Profit',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.astroFlip.buyerProfitTotalAstroFlip',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.astroFlip.buyerProfitAstroFlipArrow',
        0,
      ),
      label: 'Buyer Profit',
      type: 'currency',
    },
  ]

  const getBORAnalytics = () => [
    {
      value: get(
        property,
        'acquisition.analytics.BOR.averageAnnualReturnBOR',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.BOR.averageAnnualReturnBORArrow',
        0,
      ),
      label: 'Average Annual Return',
      type: 'percent',
    },
    {
      value: get(property, 'acquisition.analytics.BOR.IRRBOR', '-'),
      arrow: get(
        property,
        'acquisition.analytics.BOR.IRRBORArrow',
        0,
      ),
      label: 'IRR',
      type: 'percent',
    },
  ]

  const getShortTermRentalAnalytics = () => [
    {
      value:
        get(property, 'acquisition.analytics.STR.cashFlowSTR', 0) /
        12,
      arrow: get(
        property,
        'acquisition.analytics.STR.cashFlowSTRArrow',
        0,
      ),
      label: 'Net Cashflow',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.STR.cashOnCashReturnSTR',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.STR.cashOnCashReturnSTRArrow',
        0,
      ),
      label: 'CoC ROI',
      type: 'percent',
    },
    {
      value: get(property, 'acquisition.analytics.STR.IRRSTR', '-'),
      arrow: get(
        property,
        'acquisition.analytics.STR.IRRSTRArrow',
        0,
      ),
      label: 'IRR',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.STR.capRateSTR',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.STR.capRateSTRArrow',
        0,
      ),
      label: 'Yield-on-Cost',
      type: 'percent',
    },
  ]

  const getBRRRRAnalytics = () => [
    {
      value:
        get(
          property,
          'acquisition.analytics.BRRRR.cashFlowBRRRR',
          0,
        ) / 12,
      arrow: get(
        property,
        'acquisition.analytics.BRRRR.cashFlowBRRRRArrow',
        0,
      ),
      label: 'Net Cashflow',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.BRRRR.cashOnCashReturnBRRRR',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.BRRRR.cashOnCashReturnBRRRRArrow',
        0,
      ),
      label: 'CoC ROI',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.BRRRR.IRRBRRRR',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.BRRRR.IRRBRRRRArrow',
        0,
      ),
      label: 'IRR',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.BRRRR.capRateBRRRR',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.BRRRR.capRateBRRRRArrow',
        0,
      ),
      label: 'Yield-on-Cost',
      type: 'percent',
    },
  ]

  const getTurnkeyAnalytics = () => [
    {
      value:
        get(
          property,
          'acquisition.analytics.turnkey.cashFlowTurnkey',
          0,
        ) / 12,
      arrow: get(
        property,
        'acquisition.analytics.turnkey.cashFlowTurnkeyArrow',
        0,
      ),
      label: 'Net Cashflow',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.turnkey.cashOnCashReturnTurnkey',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.turnkey.cashOnCashReturnTurnkeyArrow',
        0,
      ),
      label: 'CoC ROI',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.turnkey.capRateTurnkey',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.turnkey.capRateTurnkeyArrow',
        0,
      ),
      label: 'Cap Rate',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.turnkey.IRRTurnkey',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.turnkey.IRRTurnkeyArrow',
        0,
      ),
      label: 'IRR',
      type: 'percent',
    },
  ]

  const getFixFlipAnalytics = () => [
    {
      value: get(
        property,
        'acquisition.analytics.fixFlip.saleProceeds',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.fixFlip.saleProceedsArrow',
        0,
      ),
      label: 'Net Profit',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.fixFlip.netROIFixFlip',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.fixFlip.netROIArrow',
        0,
      ),
      label: 'Net ROI',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.fixFlip.percentageOfARV',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.fixFlip.percentageOfARVArrow',
        0,
      ),
      label: 'All-In Cost to ARV',
      type: 'percent',
    },
  ]

  const getBuyHoldAnalytics = () => [
    {
      value:
        get(property, 'acquisition.analytics.buyHold.cashFlow', 0) /
        12,
      arrow: get(
        property,
        'acquisition.analytics.buyHold.cashFlowArrow',
        0,
      ),
      label: 'Net Cashflow',
      type: 'currency',
    },
    {
      value: get(
        property,
        'acquisition.analytics.buyHold.cashOnCashReturn',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.buyHold.cashOnCashReturnArrow',
        0,
      ),
      label: 'CoC ROI',
      type: 'percent',
    },
    {
      value: get(property, 'acquisition.analytics.buyHold.IRR', '-'),
      arrow: get(
        property,
        'acquisition.analytics.buyHold.IRRArrow',
        0,
      ),
      label: 'IRR',
      type: 'percent',
    },
    {
      value: get(
        property,
        'acquisition.analytics.buyHold.capRate',
        '-',
      ),
      arrow: get(
        property,
        'acquisition.analytics.buyHold.capRateArrow',
        0,
      ),
      label: 'Yield-on-Cost',
      type: 'percent',
    },
  ]

  return {
    // State
    heart,
    openCriticalFields,
    redirectToEdit,
    openAccept,
    openReject,
    openPITICashData,
    openPITICreativeData,

    // Computed values
    marketplaceOwned,
    owned,
    pricePerSqft,
    createdAt,
    daysUntilDeleted,
    listDate,
    realeflowSoldDate,
    analytics: getAnalytics(),

    // Actions
    handleOpenCriticalFields,
    handleCloseCriticalFields,
    toggleHeart,
    setOpenPITICashData,
    setOpenPITICreativeData,
    phpSend,
    updateProperty,
  }
}
